import * as Yup from 'yup';
import { useEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import { Box, Stack, Button, MenuItem } from '@mui/material';
// components
import { FormProvider, RHFSelect, RHFTextField } from '../../../../components/hook-form';
import {
  CarePlanOpportunity,
  opportunityPriorities,
  opportunitySourceTypes,
  opportunityStatus,
} from 'src/_mock/settings/_careplan-automation';
import api from 'src/ccm/services/api';
import axios from 'axios';

// ----------------------------------------------------------------------

type FormValue = Pick<CarePlanOpportunity, 'name' | 'alias' | 'sourceType' | 'priority' | 'status'>;

type OpportunitiesFormProps = {
  isEdit?: boolean;
  currentOpportunity?: CarePlanOpportunity;
};

const schema = Yup.object().shape({
  name: Yup.string().min(3).required('Name is required'),
  alias: Yup.string(),
  sourceType: Yup.string()
    .oneOf(Object.keys(opportunitySourceTypes))
    .required('Source type is required'),
  priority: Yup.string().oneOf(Object.keys(opportunityPriorities)).required('Priority is required'),
  status: Yup.string().oneOf(['active', 'inactive']).required('Name is required'),
});

const resolver = yupResolver(schema);

export default function OpportunitiesForm({ isEdit, currentOpportunity }: OpportunitiesFormProps) {
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();

  const defaultValues = useMemo(
    () =>
      ({
        name: currentOpportunity?.name || '',
        alias: currentOpportunity?.alias || '',
        sourceType: currentOpportunity?.sourceType || Object.keys(opportunitySourceTypes)[0],
        priority: currentOpportunity?.priority || Object.keys(opportunityPriorities)[0],
        status: currentOpportunity?.status || 'active',
      } as FormValue),
    [currentOpportunity]
  );

  const methods = useForm({ resolver, defaultValues });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    if (isEdit && currentOpportunity) {
      reset(defaultValues);
    }
    if (!isEdit) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, currentOpportunity]);

  const onSubmit = async (data: FormValue) => {
    try {
      if (isEdit && currentOpportunity) {
        await api.carePlan.updateOpportunity({
          ...data,
          uuid: currentOpportunity.uuid,
        });
      } else {
        await api.carePlan.createOpportunity(data);
      }

      reset();
      enqueueSnackbar(!isEdit ? 'Create success!' : 'Update success!');
      navigate('..');
    } catch (error) {
      if (axios.isAxiosError(error)) {
        enqueueSnackbar(
          error.response?.data?.message?.[0] || error?.message || 'Something went wrong!',
          {
            variant: 'error',
          }
        );
      }

      console.error(error);
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Box sx={{ p: 3 }}>
        <Stack
          gap={3}
          sx={{
            width: {
              xs: '100%',
              md: '50%',
            },
          }}
        >
          <RHFTextField name="name" multiline rows={3} label="Opportunity" />
          <RHFTextField name="alias" multiline rows={3} label="Alias Name" />

          <RHFSelect name="sourceType" label="Source Type">
            {Object.entries(opportunitySourceTypes).map(([key, value]) => (
              <MenuItem key={key} value={key}>
                {value}
              </MenuItem>
            ))}
          </RHFSelect>

          <RHFSelect name="priority" label="Priority">
            {Object.entries(opportunityPriorities).map(([key, value]) => (
              <MenuItem key={key} value={key}>
                {value}
              </MenuItem>
            ))}
          </RHFSelect>

          {isEdit && (
            <RHFSelect name="status" label="Status">
              {Object.entries(opportunityStatus).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value}
                </MenuItem>
              ))}
            </RHFSelect>
          )}
        </Stack>
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }} gap={2}>
          <Button variant="text" color="inherit" component={RouterLink} to="..">
            Cancel
          </Button>
          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            {!isEdit ? 'Create Opportunity' : 'Save Changes'}
          </LoadingButton>
        </Stack>
      </Box>
    </FormProvider>
  );
}
